import { useState, useCallback } from "react";
import { Truck, Store, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import type { CartItem } from "@/hooks/usePublicOrder";
import type { PublicRestaurant } from "@/hooks/usePublicMenu";
import { 
  publicCheckoutSchema, 
  deliveryOrderSchema, 
  formatPhoneNumber,
  type PublicCheckoutFormData 
} from "@/lib/validation-schemas";

interface PublicCheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restaurant: PublicRestaurant;
  items: CartItem[];
  subtotal: number;
  onSubmit: (data: {
    orderType: "delivery" | "pickup";
    customerName: string;
    customerPhone: string;
    deliveryAddress?: string;
    notes?: string;
  }) => Promise<boolean>;
  isSubmitting: boolean;
}

interface FormErrors {
  customerName?: string;
  customerPhone?: string;
  deliveryAddress?: string;
  notes?: string;
}

export function PublicCheckoutDialog({
  open,
  onOpenChange,
  restaurant,
  items,
  subtotal,
  onSubmit,
  isSubmitting,
}: PublicCheckoutDialogProps) {
  const [orderType, setOrderType] = useState<"delivery" | "pickup">(
    restaurant.delivery_enabled ? "delivery" : "pickup"
  );
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  const deliveryFee = orderType === "delivery" ? restaurant.delivery_fee : 0;
  const total = subtotal + deliveryFee;

  // Handle phone input with formatting
  const handlePhoneChange = useCallback((value: string) => {
    const formatted = formatPhoneNumber(value);
    setCustomerPhone(formatted);
  }, []);

  // Validate a single field
  const validateField = useCallback((field: keyof PublicCheckoutFormData, value: string) => {
    const schema = orderType === "delivery" ? deliveryOrderSchema : publicCheckoutSchema;
    const fieldData = { [field]: value };
    
    try {
      // Create a partial schema for single field validation
      const result = schema.shape[field].safeParse(value);
      if (!result.success) {
        return result.error.errors[0].message;
      }
      return undefined;
    } catch {
      return undefined;
    }
  }, [orderType]);

  // Handle field blur for validation
  const handleBlur = useCallback((field: keyof PublicCheckoutFormData) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    let value = "";
    switch (field) {
      case "customerName":
        value = customerName;
        break;
      case "customerPhone":
        value = customerPhone;
        break;
      case "deliveryAddress":
        value = deliveryAddress;
        break;
      case "notes":
        value = notes;
        break;
    }
    
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  }, [customerName, customerPhone, deliveryAddress, notes, validateField]);

  // Full form validation
  const validateForm = useCallback((): boolean => {
    const formData: PublicCheckoutFormData = {
      customerName,
      customerPhone,
      deliveryAddress: orderType === "delivery" ? deliveryAddress : undefined,
      notes: notes || undefined,
    };

    const schema = orderType === "delivery" ? deliveryOrderSchema : publicCheckoutSchema;
    const result = schema.safeParse(formData);

    if (!result.success) {
      const newErrors: FormErrors = {};
      result.error.errors.forEach(err => {
        const field = err.path[0] as keyof FormErrors;
        if (!newErrors[field]) {
          newErrors[field] = err.message;
        }
      });
      setErrors(newErrors);
      setTouched({ 
        customerName: true, 
        customerPhone: true, 
        deliveryAddress: true,
        notes: true 
      });
      return false;
    }

    setErrors({});
    return true;
  }, [customerName, customerPhone, deliveryAddress, notes, orderType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const success = await onSubmit({
      orderType,
      customerName: customerName.trim(),
      customerPhone: customerPhone.replace(/\D/g, ""), // Send only digits
      deliveryAddress: orderType === "delivery" ? deliveryAddress.trim() : undefined,
      notes: notes.trim() || undefined,
    });

    if (success) {
      onOpenChange(false);
      setCustomerName("");
      setCustomerPhone("");
      setDeliveryAddress("");
      setNotes("");
      setErrors({});
      setTouched({});
    }
  };

  const isValid = customerName.trim().length >= 2 && 
    customerPhone.replace(/\D/g, "").length >= 10 && 
    (orderType === "pickup" || deliveryAddress.trim().length >= 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Finalizar Pedido</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tipo de pedido */}
          {restaurant.delivery_enabled && restaurant.pickup_enabled && (
            <div className="space-y-2">
              <Label>Tipo de pedido</Label>
              <RadioGroup
                value={orderType}
                onValueChange={(v) => {
                  setOrderType(v as "delivery" | "pickup");
                  // Clear address error when switching to pickup
                  if (v === "pickup") {
                    setErrors(prev => ({ ...prev, deliveryAddress: undefined }));
                  }
                }}
                className="grid grid-cols-2 gap-3"
              >
                <Label
                  htmlFor="delivery"
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                    orderType === "delivery" ? "border-primary bg-primary/5" : ""
                  }`}
                >
                  <RadioGroupItem value="delivery" id="delivery" />
                  <Truck className="h-4 w-4" />
                  <span>Delivery</span>
                </Label>
                <Label
                  htmlFor="pickup"
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                    orderType === "pickup" ? "border-primary bg-primary/5" : ""
                  }`}
                >
                  <RadioGroupItem value="pickup" id="pickup" />
                  <Store className="h-4 w-4" />
                  <span>Retirada</span>
                </Label>
              </RadioGroup>
            </div>
          )}

          <Separator />

          {/* Dados do cliente */}
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="name">Seu nome *</Label>
              <Input
                id="name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                onBlur={() => handleBlur("customerName")}
                placeholder="Digite seu nome"
                maxLength={100}
                className={touched.customerName && errors.customerName ? "border-destructive" : ""}
              />
              {touched.customerName && errors.customerName && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.customerName}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">WhatsApp *</Label>
              <Input
                id="phone"
                value={customerPhone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                onBlur={() => handleBlur("customerPhone")}
                placeholder="(00) 00000-0000"
                maxLength={16}
                className={touched.customerPhone && errors.customerPhone ? "border-destructive" : ""}
              />
              {touched.customerPhone && errors.customerPhone && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.customerPhone}
                </p>
              )}
            </div>

            {orderType === "delivery" && (
              <div className="space-y-2">
                <Label htmlFor="address">Endereço de entrega *</Label>
                <Textarea
                  id="address"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  onBlur={() => handleBlur("deliveryAddress")}
                  placeholder="Rua, número, bairro, complemento..."
                  maxLength={500}
                  className={touched.deliveryAddress && errors.deliveryAddress ? "border-destructive" : ""}
                />
                {touched.deliveryAddress && errors.deliveryAddress && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.deliveryAddress}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Observações (opcional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => handleBlur("notes")}
                placeholder="Alguma observação sobre o pedido?"
                maxLength={1000}
                className={touched.notes && errors.notes ? "border-destructive" : ""}
              />
              {touched.notes && errors.notes && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.notes}
                </p>
              )}
              <p className="text-xs text-muted-foreground text-right">
                {notes.length}/1000
              </p>
            </div>
          </div>

          <Separator />

          {/* Resumo */}
          <div className="space-y-2 text-sm">
            <div className="font-medium">Resumo do pedido</div>
            {items.map((item) => (
              <div key={item.id} className="flex justify-between text-muted-foreground">
                <span>{item.quantity}x {item.name}</span>
                <span>{formatPrice(item.price * item.quantity)}</span>
              </div>
            ))}
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            {deliveryFee > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Taxa de entrega</span>
                <span>{formatPrice(deliveryFee)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-base pt-1">
              <span>Total</span>
              <span className="text-primary">{formatPrice(total)}</span>
            </div>
          </div>

          {orderType === "delivery" && restaurant.delivery_time_estimate && (
            <p className="text-xs text-muted-foreground text-center">
              Tempo estimado de entrega: {restaurant.delivery_time_estimate}
            </p>
          )}

          <Button 
            type="submit" 
            className="w-full" 
            size="lg"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              `Confirmar Pedido - ${formatPrice(total)}`
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
